import React, {
  HTMLAttributes,
  PropsWithChildren,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState
} from 'react'
import {
  DropzoneOptions,
  DropzoneProps,
  FileWithPath,
  useDropzone
} from 'react-dropzone'
import { useTranslation } from 'react-i18next'

import { IconDelete, IconRefresh, IconUpload } from '@/components/svgr/create'
import { Flex } from '@/components/common/Box'
import { Text } from '@/components/common/Text'
import { cn } from '@/lib/utils'

import { EditAvatar, Upload } from '../svgr/common'

import { Button } from './Button'
import { Dividing } from './Structure'

export interface IUploadFile extends FileWithPath {
  preview: string
}

export interface IUploadProps extends React.HTMLAttributes<HTMLDivElement> {
  // accept: string,
  name?: string
  limitText?: string
  onChange: (files: any) => void
}

export interface IUploadRef {
  rootRef: HTMLElement | null
  inputRef: HTMLElement | null
  upload: () => void
  clear: () => void
}

export const Uploader = React.forwardRef<
  IUploadRef,
  DropzoneProps & IUploadProps
>(({ className, limitText, name, onChange, ...props }, ref) => {
  const { t } = useTranslation()
  const [fileList, setFileList] = useState<IUploadFile[]>([])
  const { getRootProps, getInputProps, open, rootRef, inputRef } = useDropzone({
    // onDrop: (files: File[]) => {
    //   console.log('onDrop', acceptedFiles, list)
    // },
    onDropAccepted: (acceptedFiles: File[]) => {
      // console.log('onDropAccepted', files)
      const list: IUploadFile[] = acceptedFiles.map((file) => {
        return Object.assign(file, { preview: URL.createObjectURL(file) })
      })
      setFileList(list)
      onChange(list)
    },
    ...props
  })

  useEffect(() => {
    // Make sure to revoke the data uris to avoid memory leaks, will run on unmount
    return () => fileList.forEach((file) => URL.revokeObjectURL(file.preview))
  }, [fileList])

  useImperativeHandle(ref, () => {
    return {
      upload: () => {},
      clear: clearImage,
      rootRef: rootRef.current,
      inputRef: inputRef.current
    }
  })

  const clearImage = () => {
    setFileList([])
  }

  const deleteImage = (file: any) => {
    const index = fileList.findIndex((item) => item === file)
    fileList.splice(index, 1)
    setFileList([...fileList])
    onChange(fileList)
  }

  const SingleView: React.FC<
    PropsWithChildren<HTMLAttributes<HTMLDivElement>> & { files: IUploadFile[] }
  > = ({ files }) => {
    return (
      <div className="relative h-full">
        {files.map((file: IUploadFile) => (
          <Flex
            className="relative h-full flex-col items-center justify-between xl:flex-row"
            key={file.preview}
          >
            <div
              className="aspect-square size-full rounded-sm bg-contain bg-center bg-no-repeat"
              style={{ backgroundImage: `url(${file.preview})` }}
            >
              <img className="hidden w-full" src={file.preview} />
            </div>
            <Flex
              className={cn(
                'absolute bottom-4 w-full justify-center gap-4',
                '[&_button]:size-8 [&_button]:rounded-full [&_button]:!p-0',
                '[&_button]:border [&_button]:border-primary [&_button]:bg-white',
                '[&_button_svg]:size-4'
              )}
            >
              <Button
                outline
                onClick={() => open()}
                className="text-primary hover:text-white"
              >
                <IconRefresh />
              </Button>
              <Button
                outline
                onClick={() => deleteImage(file)}
                className="text-primary hover:text-white"
              >
                <IconDelete />
              </Button>
            </Flex>
          </Flex>
        ))}
      </div>
    )
  }

  return (
    <div className={cn('rounded-md border bg-secondary p-1 shadow', className)}>
      <div
        className="h-full w-full"
        style={{ display: fileList.length === 0 ? 'block' : 'none' }}
      >
        <div
          {...getRootProps({
            className:
              'dropzone h-full flex flex-col items-center justify-center cursor-pointer'
          })}
        >
          <span className="flex h-[60px] items-center justify-center">
            <IconUpload className="size-8 xl:size-10" />
          </span>
          <div>
            <Text variant="secondary" className="text-sm font-bold">
              Click or Drop to upload
            </Text>
            <Text
              variant="tertiary"
              className="whitespace-pre-line text-center text-sm"
            >
              {limitText}
            </Text>
          </div>
          <input {...getInputProps({ name })} />
        </div>
      </div>
      <SingleView
        files={fileList}
        style={{ display: fileList.length > 0 ? 'block' : 'none' }}
      />
    </div>
  )
})

type ImageFile = File & { preview: string }

export const ImageUploader = React.forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement> &
    DropzoneOptions & {
      single?: boolean
      defaultImage?: string
      onChange: (file?: File) => void
    }
>(({ name, single, defaultImage, onChange, ...props }, ref) => {
  const { t } = useTranslation()
  const [file, setFile] = useState<ImageFile>()

  const { getRootProps, getInputProps, open } = useDropzone({
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.bmp']
    },
    onDrop: (acceptedFiles) => {
      // console.log('>>>>>> acceptedFiles: ', acceptedFiles)
      const acceptedFile = acceptedFiles[0]
      onChange(acceptedFile)
      setFile(
        Object.assign(acceptedFile, {
          preview: URL.createObjectURL(acceptedFile)
        })
      )
    },
    noClick: true,
    ...props
  })

  const deleteImage = useCallback(() => {
    setFile(undefined)
    onChange(undefined)
  }, [onChange])

  return (
    <div
      className={cn(
        'flex flex-col gap-2 rounded-md',
        single
          ? 'bg-transparent p-0'
          : 'border bg-secondary p-2.5 xl:p-4 xl:py-2'
      )}
      {...getRootProps()}
    >
      <input type="file" ref={ref} {...getInputProps({ name })} />

      {single ? (
        <div className="relative" onClick={() => open()}>
          <img
            src={file?.preview || defaultImage}
            className="size-10 overflow-hidden rounded-full xl:size-20"
          />
          <EditAvatar className="absolute bottom-0 right-0 size-5 xl:size-8" />
        </div>
      ) : file ? (
        <div className="items-center gap-8">
          <img
            className="mx-auto my-4 h-auto w-2/5"
            src={file.preview}
            onLoad={() => {
              URL.revokeObjectURL(file.preview)
            }}
          />
          <Flex className="-mx-2.5 -mb-2.5 items-center justify-between border-t py-2 xl:-mx-4 xl:-mb-4 xl:py-4">
            <Button
              link
              className="flex-1 p-0 [&_svg]:size-4 xl:[&_svg]:size-6"
              onClick={() => deleteImage()}
            >
              <IconDelete />
              <Text className="text-sm text-primary xl:text-xl">
                {t('create.delete')}
              </Text>
            </Button>
            <Dividing orientation="vertical" className="h-6" />
            <Button
              link
              className="flex-1 p-0 [&_svg]:size-4 xl:[&_svg]:size-6"
              onClick={() => open()}
            >
              <IconRefresh />
              <Text className="text-sm text-primary xl:text-xl">
                {t('create.uploadAgain')}
              </Text>
            </Button>
          </Flex>
        </div>
      ) : (
        <Flex className="items-center gap-2" onClick={open}>
          <Upload className="xl:size-10" />
          <Text variant="tertiary" className="text-sm xl:text-lg">
            Choose a image to upload.
          </Text>
        </Flex>
      )}
    </div>
  )
})
ImageUploader.displayName = 'ImageUploader'
